import { useRef, useState, useEffect } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Badge } from "antd";

type TabType = {
    key: string;
    name: string;
    count: number;
    img?: string;
    icon?: React.ReactNode;
};

type Props = {
    tabs: TabType[];
    activeTab: string;
    onChange: (key: string) => void;
};

export default function ScrollTabs({
    tabs,
    activeTab,
    onChange,
}: Props) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        setCanLeft(el.scrollLeft > 0);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, []);

    const scroll = (dir: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;

        const amount = el.clientWidth * 0.7;

        el.scrollBy({
            left: dir === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    return (
        <div className="relative rounded-xl bg-white dark:bg-[#1f2937] border border-gray-200 dark:border-gray-700">

            {/* LEFT BUTTON */}
            {canLeft && (
                <button
                    onClick={() => scroll("left")}
                    className="
                        absolute left-2 top-1/2 translate-y-50 z-20
                        w-8 h-8 flex items-center justify-center
                        rounded-full bg-white dark:bg-[#374151]
                        shadow-md hover:bg-gray-100 dark:hover:bg-gray-600
                        transition t-text-white
                    "
                >
                    <LeftOutlined />
                </button>
            )}

            {/* RIGHT BUTTON */}
            {canRight && (
                <button
                    onClick={() => scroll("right")}
                    className="
                        absolute right-2 top-1/2 translate-y-50 z-20
                        w-8 h-8 flex items-center justify-center
                        rounded-full bg-white dark:bg-[#374151]
                        shadow-md hover:bg-gray-100 dark:hover:bg-gray-600
                        transition t-text-white
                    "
                >
                    <RightOutlined />
                </button>
            )}

            {/* TAB LIST */}
            <div
                ref={scrollRef}
                onScroll={checkScroll}
                className={`
                    flex gap-3 p-3
                    overflow-x-auto whitespace-nowrap
                    scrollbar-hide
                    ${canLeft ? "pl-12" : ""}
                    ${canRight ? "pr-12" : ""}
                `}
            >
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.key;

                    return (
                        <div
                            key={tab.key}
                            onClick={() => onChange(tab.key)}
                            className={`
                                px-3 py-2 rounded-lg flex items-center gap-4 cursor-pointer
                                transition-all duration-200 relative text-sm font-medium flex-shrink-0 relative
                                ${isActive
                                    ? "bg-yellow-100 text-[#357f96] dark:bg-yellow-900 dark:text-yellow-300 shadow-sm"
                                    : "text-gray-600 text-000000e0 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }
                            `}
                        >
                            <div className="flex items-center gap-2">
                                {tab.img ? (
                                    <img
                                        src={tab.img}
                                        alt={tab.name}
                                        className="w-[20px] h-[20px] rounded-sm object-cover"
                                    />
                                ) : (
                                    <span className="w-[20px] h-[20px] flex items-center justify-center text-[18px]">
                                        {tab.icon}
                                    </span>
                                )}
                                {tab.name}
                            </div>
                            <Badge
                                count={tab.count}
                                className={`
                                    [&_.ant-badge-count]:min-w-4
                                    [&_.ant-badge-count]:!leading-4
                                    [&_.ant-badge-count]:flex
                                    [&_.ant-badge-count]:items-center
                                    [&_.ant-badge-count]:justify-center
                                    [&_.ant-badge-count]:rounded-full
                                    text-xs
                                    [&_.ant-badge-count]:!h-4
                                    [&_.ant-badge-count]:!min-h-4
                                    dark:[&_.ant-badge-count]:!bg-gray-600
                                    dark:[&_.ant-badge-count]:!text-white
                                    ${isActive
                                        ? "[&_.ant-badge-count]:!bg-[#357f96] [&_.ant-badge-count]:!text-white"
                                        : "[&_.ant-badge-count]:!bg-gray-200 [&_.ant-badge-count]:!text-black dark:[&_.ant-badge-count]:!bg-gray-600 dark:[&_.ant-badge-count]:!text-white"
                                    }
                                `}
                            />
                            <div className="filter-platform-highlight" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}